// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewAttentionBanner.test.tsx
/**
 * `## Behavior impact` — declared here, because the PR body's Before/After pair
 * describes `focusedCitedClaims` rather than this file, and this banner ships
 * bundled with four unrelated Ask Puntt changes (#5886 review).
 *
 * Before: pressing Continue when christo answered `accepted: false` with an
 * EMPTY `validationErrors` painted nothing at all. The error row was keyed off
 * `validationErrors[0]`, so a cancelled episode or a dispatch that never
 * resolved read as a dead button. The button also re-armed the moment the POST
 * settled, so a rerun that was merely QUEUED (`state: "continuation_pending"`,
 * which does not invalidate the episode query) could be fired a second time
 * against an episode the cache still reported as "open".
 *
 * After: a non-accepted reply that is not still queued shows the failure row —
 * the server's own validation message when it sent one, the generic copy when
 * it did not. The button stays disabled on the "starting" label while the POST
 * is in flight or while either the episode or the mutation result reads
 * `continuation_pending`. Both reads are matched on `episodeId` first, so a
 * result that answered a PREVIOUS episode no longer disables or errors the
 * episode now on screen; this banner is reconciled without a `key` across chats
 * and across episode refetches, and React Query keeps that `data` around.
 *
 * The four other source changes bundled into the same PR:
 * - `services/askPuntt.ts`: the review-attention clients. `continue` POSTs the
 *   caller's `idempotencyKey`, so a double press cannot start two reruns.
 * - `components/ask-puntt-thread/AskPunttThread.tsx`: the agent's question form
 *   is answerable on the last RENDERED row instead of the last index, so a
 *   dropped trailing empty assistant placeholder no longer locks the form the
 *   agent just drew read-only.
 * - `routes/ask-puntt/components/CitedClaimsPanel.tsx`: the show-all effect
 *   watches `selectedRuleId`, so a `[n]` marker on a DIFFERENT answer that
 *   resolves to the same card index still opens the cut list and scrolls to it.
 * - `routes/ask-puntt/utils/focusedCitedClaims.ts`: `withoutRelatedClaims` also
 *   drops the `claim:<ruleId>` graph node of a rule whose chip it hid, so a
 *   rule the answer only matched is no longer re-listed under "Cited claims".
 */
import { WarningCircle } from "@phosphor-icons/react";
import { Button } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import {
  useAskPunttReviewAttention,
  useContinueAskPunttReviewAttention,
} from "../../utils/queries/askPuntt";

export function AskV2ReviewAttentionBanner({ chatId }: { chatId: string }) {
  const { t } = useTranslation("ui");
  const query = useAskPunttReviewAttention(chatId);
  const episode = query.data?.episode;
  const continuation = useContinueAskPunttReviewAttention(chatId);
  if (!episode) return null;

  // The continue mutation only invalidates the episode query when the server
  // ACCEPTED the continuation, so after a non-accepted reply the cached episode
  // still reads "open". Trust the mutation result as well as the cache:
  //  - `state: "continuation_pending"` means the rerun is queued for the server
  //    sweep, so the button must stay disabled instead of re-arming a duplicate
  //    POST against a stale "open" episode.
  //  - `accepted: false` is reachable with an EMPTY `validationErrors` (a
  //    cancelled episode, or a dispatch that never resolved). Keying the error
  //    row off `validationErrors[0]` alone rendered no feedback at all there:
  //    the click silently did nothing.
  // React Query keeps a mutation's `data` for the lifetime of the hook
  // instance, and this banner is reconciled (no `key`) across chats and across
  // episode refetches. A result that answered a PREVIOUS episode must not
  // disable or error the episode now on screen, so scope it by `episodeId`.
  const latest = continuation.data;
  const result = latest?.episodeId === episode.episodeId ? latest : undefined;
  const validationError = result?.validationErrors[0];
  const starting =
    continuation.isPending ||
    episode.state === "continuation_pending" ||
    result?.state === "continuation_pending";
  const failed =
    continuation.isError ||
    (result !== undefined && !result.accepted && !starting);
  return (
    <section
      className="border-amber-7 bg-amber-2 mx-4 mt-3 rounded-lg border p-3 text-puntt-neutral-gray-12"
      data-testid="ask-v2-review-needs-attention"
      aria-labelledby="ask-v2-review-needs-attention-title"
    >
      <div className="flex items-start gap-2">
        <WarningCircle
          size={18}
          weight="fill"
          className="text-amber-10 mt-0.5 shrink-0"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <h2
            id="ask-v2-review-needs-attention-title"
            className="text-sm font-semibold"
          >
            {t("askV2.reviewAttention.title")}
          </h2>
          <p className="mt-1 text-xs text-puntt-neutral-gray-11">
            {t("askV2.reviewAttention.description")}
          </p>
          <Button
            type="button"
            size="1"
            className="mt-2 cursor-pointer"
            data-testid="ask-v2-review-needs-attention-continue"
            aria-label={t("askV2.reviewAttention.runFullReview")}
            disabled={starting}
            onClick={() =>
              continuation.mutate({ episodeId: episode.episodeId })
            }
          >
            {starting
              ? t("askV2.reviewAttention.starting")
              : t("askV2.reviewAttention.runFullReview")}
          </Button>
          {validationError || failed ? (
            <p className="text-tomato-11 mt-2 text-xs" role="alert">
              {validationError ?? t("askV2.reviewAttention.failed")}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}
